import { useEffect, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import {
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  findBase,
  resolveApiBaseUrl,
  splitHost,
  suggestApiHost,
} from './apiDomain';

export type DomainEndpoint = 'frontend' | 'api';
export type ApplicationDomains = Record<
  string,
  Partial<Record<DomainEndpoint, string>>
>;

const SPA_FRAMEWORKS = ['react', 'angular', 'vue'];
const SSR_FRAMEWORKS = ['nextjs', 'aspnet'];

/**
 * A single-page app that also has an API.
 *
 * The two run on two host names: the single-page app on the Static Web App, the
 * API on the App Service behind Front Door. The wizard therefore asks for both,
 * and the Auth0 client is created against the API host.
 */
export function isSpaWithApi(
  enableStaticWeb: boolean,
  enableApiService: boolean,
  frontend: string | undefined,
): boolean {
  return (
    enableStaticWeb &&
    enableApiService &&
    SPA_FRAMEWORKS.includes(frontend ?? '')
  );
}

/**
 * A server-rendered frontend with a separate API service.
 *
 * The portal cannot build this shape. One skeleton is generated for the whole
 * application, and the SSR skeletons carry no `api` project and no
 * `src/api/server.ts`. The backend split then runs against files that are not
 * there: the Express split deletes the application source and reports success,
 * and the .NET split throws only after the Auth0 pull request is merged and
 * onboarding has started.
 *
 * So the combination is refused while the user is still choosing, and refused
 * again in `starter:repositories:prepare` for a form that is bypassed.
 */
export function isUnsupportedSsrWithApi(
  enableStaticWeb: boolean,
  enableApiService: boolean,
  frontend: string | undefined,
): boolean {
  return (
    enableStaticWeb &&
    enableApiService &&
    SSR_FRAMEWORKS.includes(frontend ?? '')
  );
}

export const SSR_WITH_API_ERROR =
  'A server-rendered frontend (Next.js or ASP.NET) cannot have a separate API ' +
  'service. Go back to Technology Stack. Turn off "Do you want an API ' +
  'service?", or choose React, Angular or Vue.';

export function getDomainEndpoints(
  enableStaticWeb: boolean,
  enableApiService: boolean,
): DomainEndpoint[] {
  const endpoints: DomainEndpoint[] = [];
  if (enableStaticWeb) endpoints.push('frontend');
  if (enableApiService) endpoints.push('api');
  return endpoints;
}

/**
 * The host names an Auth0 application cannot do without, or an empty list when
 * it gets no Auth0 client.
 *
 * The callback and logout URLs are built from these host names. An environment
 * that lacks one produces an application which reaches Auth0 and then fails on
 * the way back, so the wizard asks before anything is created.
 *
 * A SPA plus API application needs BOTH: the API host signs the user in and
 * owns the session cookie, and the frontend host is where Auth0 sends the
 * browser afterwards and the only origin the API lets through CORS.
 */
export function getRequiredDomainEndpoints(
  enableAuth0: boolean,
  enableStaticWeb: boolean,
  enableApiService: boolean,
  frontend: string | undefined,
): DomainEndpoint[] {
  if (!enableAuth0) return [];
  if (enableStaticWeb && SSR_FRAMEWORKS.includes(frontend ?? '')) {
    return ['frontend'];
  }
  if (isSpaWithApi(enableStaticWeb, enableApiService, frontend)) {
    return ['frontend', 'api'];
  }
  if (enableApiService && !enableStaticWeb) return ['api'];
  return [];
}

/** The form the Modify wizard submits carries the untouched original. */
export function isDay2Form(formData: Record<string, any>): boolean {
  return (
    formData._original !== null &&
    typeof formData._original === 'object' &&
    formData._original !== undefined
  );
}

/**
 * The environments this run ADDS, and so the only ones a Modify run may demand
 * a host name for. An environment the application already had may well have
 * none: it was created before the Domain step existed.
 */
export function addedEnvironments(formData: Record<string, any>): string[] {
  const after: string[] = Array.isArray(formData.environments)
    ? formData.environments.filter((e: unknown) => typeof e === 'string')
    : [];
  if (!isDay2Form(formData)) return after;
  const before: unknown = formData._original?.environments;
  const known: string[] = Array.isArray(before)
    ? before.filter((e: unknown): e is string => typeof e === 'string')
    : [];
  return after.filter(environment => !known.includes(environment));
}

export function normalizeDomainFormData(
  value: ApplicationDomains | undefined,
  environments: string[],
  endpoints: DomainEndpoint[],
): ApplicationDomains {
  return Object.fromEntries(
    environments.map(environment => [
      environment,
      Object.fromEntries(
        endpoints
          .map(endpoint => [endpoint, value?.[environment]?.[endpoint] ?? ''])
          .filter(([, domain]) => domain !== ''),
      ),
    ]),
  );
}

const HOSTNAME_RE =
  /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]([a-z0-9-]{0,61}[a-z0-9])?$/i;

/** The example each box shows, so a person can tell which host name is which. */
export function exampleHost(endpoint: DomainEndpoint | undefined): string {
  return endpoint === 'api'
    ? 'my-app-api-uat.besmart.software'
    : 'my-app-uat.besmart.software';
}

export function getDomainError(
  raw: string | undefined,
  endpoint?: DomainEndpoint,
): string | undefined {
  const value = (raw ?? '').trim();
  if (!value) return undefined;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    return 'Enter only the host name, without http:// or https://.';
  }
  if (value.includes('/')) {
    return 'Enter only the host name, without a trailing slash or path.';
  }
  if (/\s/.test(value)) {
    return 'Host name must not contain spaces.';
  }
  if (/:\d+$/.test(value)) {
    return 'Enter only the host name, without a port.';
  }
  if (!HOSTNAME_RE.test(value)) {
    return `Enter a valid host name, for example ${exampleHost(endpoint)}.`;
  }
  return undefined;
}

const ENVIRONMENT_LABELS: Record<string, string> = {
  uat: 'UAT',
  staging: 'Staging',
  'prod-eu': 'Production - Europe',
  'prod-us': 'Production - United States',
  'prod-anz': 'Production - Australia',
};

function environmentLabel(environment: string): string {
  return ENVIRONMENT_LABELS[environment] ?? environment;
}

function endpointLabel(endpoint: string): string {
  return endpoint === 'frontend' ? 'Web frontend' : 'API';
}

export interface AttachedDomainError {
  environment: string;
  endpoint: DomainEndpoint;
  /** The message without its `<Environment> <Endpoint>: ` prefix. */
  text: string;
}

/**
 * Finds the box a validation message belongs to.
 *
 * Every message the validator produces about one host name starts with the
 * label of that box — `UAT Web frontend: …` — because a field-level
 * validation has no other way to say which box it means. The component turns
 * that prefix back into a box so the message is shown once, under the box it
 * is about, and not repeated as a field-wide error below the whole step.
 *
 * A message that names no box (the SSR + API refusal, or an environment that
 * is no longer selected) returns `undefined` and is shown below the step.
 */
export function attachDomainError(
  message: string,
  environments: string[] = Object.keys(ENVIRONMENT_LABELS),
): AttachedDomainError | undefined {
  for (const environment of environments) {
    for (const endpoint of ['frontend', 'api'] as DomainEndpoint[]) {
      const prefix = `${environmentLabel(environment)} ${endpointLabel(
        endpoint,
      )}: `;
      if (message.startsWith(prefix)) {
        return { environment, endpoint, text: message.slice(prefix.length) };
      }
    }
  }
  return undefined;
}

/** The frontend/API pairs that are filled in, keyed by frontend host name. */
export function domainPairs(
  value: ApplicationDomains | undefined,
  environments?: string[],
): Record<string, string> {
  const table: Record<string, string> = {};
  for (const [environment, endpoints] of Object.entries(value ?? {})) {
    if (environments && !environments.includes(environment)) continue;
    const frontend = (endpoints?.frontend ?? '').trim().toLowerCase();
    const api = (endpoints?.api ?? '').trim().toLowerCase();
    if (frontend && api) table[frontend] = `https://${api}`;
  }
  return table;
}

/**
 * What the Domain step says out loud but does not refuse.
 *
 * Neither of these makes the application being created wrong today. The first
 * costs an environment added LATER its automatic API address, and the second
 * costs the session its cookie — both are real, and both are the user's call to
 * make, so they are said here and not raised as errors.
 */
export function getDomainWarnings(
  value: ApplicationDomains | undefined,
  environments: string[],
): string[] {
  const warnings: string[] = [];

  const bases = new Set<string>();
  let unreadablePair = false;
  for (const environment of environments) {
    const frontend = (value?.[environment]?.frontend ?? '').trim().toLowerCase();
    const api = (value?.[environment]?.api ?? '').trim().toLowerCase();
    if (!frontend || !api) continue;

    const frontendParts = splitHost(frontend);
    const apiParts = splitHost(api);

    // Different zone means a different site, and a session cookie written by
    // the API is then not sent with the frontend's calls. Sign-in completes and
    // the application still shows nobody signed in.
    if (frontendParts.zone !== apiParts.zone) {
      warnings.push(
        `${environmentLabel(environment)}: the web frontend (${frontend}) and ` +
          `the API (${api}) are on different domains, so the browser treats ` +
          `them as different sites and will not send the session cookie. ` +
          `Sign-in will appear to succeed and the application will still show ` +
          `nobody signed in. Put both on the same domain.`,
      );
    }

    const base = findBase(frontendParts.label, apiParts.label);
    if (base === undefined) unreadablePair = true;
    else bases.add(base);
  }

  if (unreadablePair || bases.size > 1) {
    warnings.push(
      'The API host name does not follow the "<name>-api-<environment>" ' +
        'convention, so an environment added later will not get its API ' +
        'address derived automatically. The application being created now is ' +
        'unaffected; a later environment needs its API host name entered by ' +
        'hand, and the frontend needs an entry adding to ' +
        'src/infrastructure/config/apiBaseUrl.ts.',
    );
  }

  return warnings;
}

export const applicationDomainsValidation: CustomFieldValidator<
  ApplicationDomains
> = (value, validation, context) => {
  const formData = context?.formData ?? {};

  // Refused before the run creates anything. The wizard offers the rendering
  // model and the API service as two independent answers, and nothing else
  // stops a person from giving both. Every result of that pair is a broken
  // application, so this is the first point that can say so.
  if (
    isUnsupportedSsrWithApi(
      formData.enable_static_web === true,
      formData.enable_app_service === true,
      typeof formData.frontend === 'string' ? formData.frontend : undefined,
    )
  ) {
    validation.addError(SSR_WITH_API_ERROR);
    return;
  }

  for (const [environment, endpoints] of Object.entries(value ?? {})) {
    for (const [endpoint, domain] of Object.entries(endpoints ?? {})) {
      const error = getDomainError(domain, endpoint as DomainEndpoint);
      if (error) {
        validation.addError(
          `${environmentLabel(environment)} ${endpointLabel(
            endpoint,
          )}: ${error}`,
        );
        return;
      }
    }
  }

  const environments: string[] = Array.isArray(formData.environments)
    ? formData.environments.filter((e: unknown) => typeof e === 'string')
    : Object.keys(value ?? {});

  // One host name, one environment. Two environments pointing at the same host
  // is a Front Door custom domain that cannot exist twice, and the second
  // `terraform apply` is where it would be found out.
  const seen = new Map<string, string>();
  for (const environment of environments) {
    for (const endpoint of ['frontend', 'api'] as DomainEndpoint[]) {
      const domain = (value?.[environment]?.[endpoint] ?? '')
        .trim()
        .toLowerCase();
      if (!domain) continue;
      const owner = seen.get(domain);
      if (owner) {
        validation.addError(
          `${environmentLabel(environment)} ${endpointLabel(endpoint)}: ` +
            `${domain} is already used by ${owner}. Each host name belongs to ` +
            `one environment.`,
        );
        return;
      }
      seen.set(domain, `${environmentLabel(environment)} ${endpointLabel(endpoint)}`);
    }
  }

  // The Auth0 client is created per environment from the host names entered
  // here. Without them the run produces an application that cannot complete a
  // sign-in, and the only way back is a second run. This is the first point
  // that knows, so this is where it stops.
  const required = getRequiredDomainEndpoints(
    formData.enable_auth0 === true,
    formData.enable_static_web === true,
    formData.enable_app_service === true,
    typeof formData.frontend === 'string' ? formData.frontend : undefined,
  );
  if (!required.length) return;

  // Day-1 demands every environment. A Modify run demands only what it ADDS:
  // an environment the application already had may have been created before
  // this step existed, and refusing it would make a viewer-list change
  // impossible. `auth0:resolve-tenant` draws the same line.
  const mustHaveDomains = isDay2Form(formData)
    ? addedEnvironments(formData)
    : environments;

  for (const environment of mustHaveDomains) {
    for (const endpoint of required) {
      const domain = (value?.[environment]?.[endpoint] ?? '').trim();
      if (domain === '') {
        validation.addError(
          `${environmentLabel(environment)} ${endpointLabel(
            endpoint,
          )}: Enter the host name that sign-in uses for this environment.`,
        );
        return;
      }
    }
  }

  // A Modify run that adds an environment to a SPA plus API application has a
  // second constraint the first run did not: the frontend was BUILT once, with
  // the pairs that existed then, and it derives the address of anything added
  // later from them. An API host the running code cannot derive is a frontend
  // that calls its own origin and gets the index page back.
  if (!isDay2Form(formData) || !required.includes('api')) return;

  const knownEnvironments = environments.filter(
    environment => !mustHaveDomains.includes(environment),
  );
  const table = domainPairs(value, knownEnvironments);
  if (!Object.keys(table).length) return;

  for (const environment of mustHaveDomains) {
    const frontend = (value?.[environment]?.frontend ?? '').trim().toLowerCase();
    const api = (value?.[environment]?.api ?? '').trim().toLowerCase();
    if (!frontend || !api) continue;
    const derived = resolveApiBaseUrl(frontend, table);
    if (derived && derived !== `https://${api}`) {
      validation.addError(
        `${environmentLabel(environment)} ${endpointLabel('api')}: the ` +
          `frontend already deployed derives its API address from the ` +
          `environments this application already has, and for ${frontend} ` +
          `that is ${derived.replace(/^https:\/\//, '')}. Enter that host ` +
          `name, or the new environment's frontend will call an address that ` +
          `does not answer.`,
      );
      return;
    }
  }
};

export const ApplicationDomainsField = (
  props: FieldExtensionComponentProps<ApplicationDomains>,
) => {
  const { formData, onChange, rawErrors, schema } = props;
  const fullFormData =
    ((props as any).formContext?.formData as
      | {
          environments?: string[];
          enable_static_web?: boolean;
          enable_app_service?: boolean;
        }
      | undefined) ?? {};
  const environments = useMemo(
    () => fullFormData.environments ?? [],
    [fullFormData.environments],
  );
  const enableStaticWeb = !!fullFormData.enable_static_web;
  const enableApiService = !!fullFormData.enable_app_service;
  const endpoints = useMemo(
    () => getDomainEndpoints(enableStaticWeb, enableApiService),
    [enableStaticWeb, enableApiService],
  );
  const hasConfigurableDomains = endpoints.length > 0;

  useEffect(() => {
    const normalized = normalizeDomainFormData(
      formData,
      environments,
      endpoints,
    );
    if (JSON.stringify(normalized) !== JSON.stringify(formData ?? {})) {
      onChange(normalized);
    }
  }, [endpoints, environments, formData, onChange]);

  const update = (
    environment: string,
    endpoint: DomainEndpoint,
    value: string,
  ) => {
    const current = formData?.[environment] ?? {};
    const next: Partial<Record<DomainEndpoint, string>> = {
      ...current,
      [endpoint]: value,
    };

    // Typing the frontend fills the API in for you. It keeps following along
    // while the API box still holds what this same rule produced, and stops
    // the moment the box is edited by hand, so a host name that does not
    // follow the convention is never overwritten.
    if (endpoint === 'frontend' && endpoints.includes('api')) {
      // The pairs of the OTHER environments: the suggestion then matches what
      // the generated frontend derives from them at run time.
      const knownPairs = domainPairs(
        formData,
        environments.filter(other => other !== environment),
      );
      const api = current.api ?? '';
      if (
        api === '' ||
        api === suggestApiHost(current.frontend ?? '', knownPairs)
      ) {
        next.api = suggestApiHost(value, knownPairs);
      }
    }

    onChange({ ...(formData ?? {}), [environment]: next });
  };

  const warnings = getDomainWarnings(formData, environments);

  // Each validation message is shown exactly once: under the box it names when
  // that box is on screen, otherwise below the step. The templates set
  // `ui:hideError` on this field, so nothing else renders `rawErrors`.
  const attachedErrors: AttachedDomainError[] = [];
  const unattachedErrors: string[] = [];
  for (const message of rawErrors ?? []) {
    const attached = attachDomainError(message, environments);
    if (
      attached &&
      hasConfigurableDomains &&
      endpoints.includes(attached.endpoint)
    ) {
      attachedErrors.push(attached);
    } else {
      unattachedErrors.push(message);
    }
  }
  const attachedError = (environment: string, endpoint: DomainEndpoint) =>
    attachedErrors.find(
      e => e.environment === environment && e.endpoint === endpoint,
    )?.text;

  return (
    <FormControl fullWidth error={!!rawErrors?.length}>
      <Typography variant="h6">{schema.title ?? 'Domains'}</Typography>
      <FormHelperText>
        {schema.description ??
          'Configure the public DNS names for each selected environment.'}
      </FormHelperText>

      {!hasConfigurableDomains && (
        <Box mt={2}>
          <FormHelperText>
            No web frontend or API service was selected, so there is no public
            domain to configure here. You can skip this step.
          </FormHelperText>
        </Box>
      )}

      {hasConfigurableDomains &&
        environments.map(environment => (
          <Box key={environment} mt={3}>
            <Typography variant="subtitle1">
              {environmentLabel(environment)} domain
            </Typography>
            {endpoints.map(endpoint => {
              const value = formData?.[environment]?.[endpoint] ?? '';
              const error =
                getDomainError(value, endpoint) ??
                attachedError(environment, endpoint);
              return (
                <Box key={endpoint} mt={2}>
                  <TextField
                    fullWidth
                    label={endpointLabel(endpoint)}
                    placeholder={
                      endpoint === 'frontend'
                        ? `app-${environment}.example.com`
                        : `app-api-${environment}.example.com`
                    }
                    value={value}
                    error={!!error}
                    helperText={
                      error ??
                      (endpoint === 'api'
                        ? 'Host name only, for example app-api-uat.besmart.software'
                        : 'Host name only, for example app-uat.besmart.software')
                    }
                    onChange={event =>
                      update(environment, endpoint, event.target.value)
                    }
                    // Trim + lowercase on blur rather than on change so the
                    // caret does not jump while typing. Host names are
                    // case-insensitive, and a stable casing keeps the derived
                    // Front Door resource names identical across runs.
                    onBlur={event =>
                      update(
                        environment,
                        endpoint,
                        event.target.value.trim().toLowerCase(),
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    // The label alone does not reach the input: this control
                    // renders without a `for`, so a screen reader — and a test
                    // — would find two unnamed boxes per environment.
                    inputProps={{
                      'aria-label': `${environmentLabel(
                        environment,
                      )} ${endpointLabel(endpoint)}`,
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        ))}

      {hasConfigurableDomains && environments.length === 0 && (
        <FormHelperText>Select at least one environment first.</FormHelperText>
      )}
      {warnings.map(warning => (
        <Box key={warning} mt={2}>
          <FormHelperText>{`Warning: ${warning}`}</FormHelperText>
        </Box>
      ))}
      {unattachedErrors.map(message => (
        <FormHelperText key={message} error>
          {message}
        </FormHelperText>
      ))}
    </FormControl>
  );
};
